import { createApi } from '@reduxjs/toolkit/query/react';
import { type ObjectiveType } from '@strata/react-status-icons';
import { type OnboardingStatus } from '@strata/react-status-indicators';

import { ONBOARDING_API_URL } from 'api/APIBase';
import { handleError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface ShippingInfo {
    tracking_number: string;
    shipment_date?: string;
    shipment_carrier?: string;
}

export const onboardingApi = createApi({
    baseQuery: getBaseQuery(ONBOARDING_API_URL),
    reducerPath: 'onboardingApi',
    endpoints: (build) => ({
        getShippingInfo: build.query<ShippingInfo, { referralId: string }>({
            query: ({ referralId }) => `shipping/${referralId}`,
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving shipping information.', err);
                }
            },
        }),
        getOnboardingStatusMap: build.query<Record<string, OnboardingStatus>, void>({
            query: () => 'client/onboarding_status/',
            transformResponse: (response: Record<string, OnboardingStatus>) =>
                // Conform the keys to one case to ensure consistent lookup
                Object.fromEntries(
                    Object.entries(response).map(([key, value]) => [key.toLowerCase(), value]),
                ),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving onboarding information.', err);
                }
            },
        }),
        getObjectiveStatuses: build.query<
            Record<ObjectiveType, OnboardingStatus>,
            { referralId: string }
        >({
            query: ({ referralId }) => `client/objective_status/${referralId}`,
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving onboarding information.', err);
                }
            },
        }),
    }),
});

export const {
    useGetShippingInfoQuery,
    useGetOnboardingStatusMapQuery,
    useGetObjectiveStatusesQuery,
} = onboardingApi;
